import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Sparkles, Loader2, Eye, EyeOff, Crown, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function Signup() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const selectedPlan = searchParams.get('plan');

    // Redirect to plan selection if no plan is chosen
    useEffect(() => {
        if (!selectedPlan || !['essential', 'premium'].includes(selectedPlan)) {
            navigate('/plans');
        }
    }, [selectedPlan, navigate]);

    const passwordRequirements = [
        { met: password.length >= 8, text: 'At least 8 characters' },
        { met: /[A-Z]/.test(password), text: 'One uppercase letter' },
        { met: /[0-9]/.test(password), text: 'One number' }
    ];

    const planDetails = {
        essential: { name: 'Essential', price: '$9/month', color: 'bg-teal-500' },
        premium: { name: 'Premium', price: '$19/month', color: 'bg-violet-500' }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        if (password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }

        setLoading(true);

        try {
            const user = await register(name, email, password, selectedPlan);
            if (user.is_founding_member) {
                toast.success('Welcome, Founding Member! You\'re among the first 300 users!');
            } else {
                toast.success('Account created successfully!');
            }
            navigate('/dashboard');
        } catch (error) {
            const message = error.response?.data?.detail || 'Failed to create account. Please try again.';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    // Don't render if no valid plan
    if (!selectedPlan || !['essential', 'premium'].includes(selectedPlan)) {
        return null;
    }

    const currentPlan = planDetails[selectedPlan];

    return (
        <div className="min-h-screen flex gradient-bg">
            {/* Left side - Branding */}
            <div className="hidden lg:flex lg:w-1/2 flex-col justify-center items-center p-12 relative overflow-hidden">
                <div 
                    className="absolute inset-0 opacity-10"
                    style={{
                        backgroundImage: 'url(https://images.unsplash.com/photo-1672659573664-6b224cb27fed?crop=entropy&cs=srgb&fm=jpg&q=85)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                    }}
                />
                <div className="relative z-10 text-center max-w-md">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center mx-auto mb-8 shadow-xl shadow-primary/30">
                        <Sparkles className="w-10 h-10 text-white" />
                    </div>
                    <h1 className="font-heading text-4xl font-bold mb-4 gradient-text">
                        Start Your Journey
                    </h1>
                    <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                        Join thousands of adults finding support, tracking progress, and building community.
                    </p>
                    
                    {/* Founding Member Badge */}
                    <div className="glass-card p-4 rounded-2xl inline-flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                            <Crown className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div className="text-left">
                            <p className="font-semibold text-sm">Founding Member Perks</p>
                            <p className="text-xs text-muted-foreground">First 300 users get 30% off forever</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right side - Signup Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-6">
                <Card className="w-full max-w-md border-0 shadow-xl bg-card/80 backdrop-blur-xl">
                    <CardHeader className="text-center space-y-2">
                        {/* Mobile logo */}
                        <div className="lg:hidden w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary/30">
                            <Sparkles className="w-7 h-7 text-white" />
                        </div>
                        <CardTitle className="font-heading text-2xl">Create Account</CardTitle>
                        <CardDescription>
                            Start your autoimmune support journey
                        </CardDescription>
                        {/* Selected Plan Badge */}
                        <div className="pt-2">
                            <Badge className={`${currentPlan.color} text-white px-4 py-1`}>
                                {currentPlan.name} Plan - {currentPlan.price}
                            </Badge>
                            <p className="text-xs text-muted-foreground mt-2">
                                <Link to="/plans" className="text-primary hover:underline">
                                    Change plan
                                </Link>
                            </p>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Full Name</Label>
                                <Input
                                    id="name"
                                    type="text"
                                    placeholder="Your name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    className="h-11"
                                    data-testid="signup-name"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="h-11"
                                    data-testid="signup-email"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password">Password</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="h-11 pr-10"
                                        data-testid="signup-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="w-4 h-4" />
                                        ) : (
                                            <Eye className="w-4 h-4" />
                                        )}
                                    </button>
                                </div>
                                {password && (
                                    <div className="space-y-1 mt-2">
                                        {passwordRequirements.map((req, i) => (
                                            <div 
                                                key={i} 
                                                className={`flex items-center gap-2 text-xs ${
                                                    req.met ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'
                                                }`}
                                            >
                                                <Check className={`w-3 h-3 ${req.met ? 'opacity-100' : 'opacity-30'}`} />
                                                {req.text}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">Confirm Password</Label>
                                <Input
                                    id="confirmPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    className="h-11"
                                    data-testid="signup-confirm-password"
                                />
                                {confirmPassword && password !== confirmPassword && (
                                    <p className="text-xs text-destructive">Passwords do not match</p>
                                )}
                            </div>
                            <Button
                                type="submit"
                                className="w-full h-11 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-medium shadow-lg shadow-primary/25 mt-2"
                                disabled={loading}
                                data-testid="signup-submit"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Creating account...
                                    </>
                                ) : (
                                    'Create Account'
                                )}
                            </Button>
                        </form>

                        <div className="mt-6 text-center">
                            <p className="text-sm text-muted-foreground">
                                Already have an account?{' '}
                                <Link
                                    to="/login"
                                    className="font-medium text-primary hover:underline"
                                    data-testid="login-link"
                                >
                                    Sign in
                                </Link>
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
